import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Leaf, Timer, Store } from "lucide-react";
import { FadeUp, MaskedLine, ParallaxImage, ClipReveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";

const HERO_IMG = "/catalog/cat-primi.jpg";
const STORE_IMG = "/risorice/foodbox.jpg";
const MARKET_IMG = "/risorice/kiosk.jpg";

const homeStats = [
  { value: "+30%", label: "Crescita annua del mercato" },
  { value: "84", label: "Referenze a catalogo" },
  { value: "5 min", label: "Pronto in tavola" },
  { value: "365 gg", label: "Shelf-life garantita" },
];

const chapters = [
  {
    n: "01",
    title: "Propaq",
    text: "84 referenze di piatti pronti e sughi gluten free, pronte in 5 minuti: shelf-life di 365 giorni, zero sprechi, margini da vero prodotto premium.",
    to: "/propaq",
    cta: "Scopri il marchio",
    icon: Leaf,
    testId: "chapter-propaq",
  },
  {
    n: "02",
    title: "Risorice",
    text: "Il ristorante automatizzato che si serve da solo: MAGNETRON, Food-Box Wall e AI. Chiavi in mano da € 90k, ti richiamiamo entro 48 ore.",
    to: "/affiliazione",
    cta: "Apri il tuo negozio",
    icon: Store,
    testId: "chapter-risorice",
  },
];

const Home = () => (
  <div data-testid="home-page">
    <Seo
      title="COMMPAQ — Propaq Gluten Free e Franchising Risorice"
      description="Piatti pronti Propaq 100% gluten free e Risorice, il ristorante automatizzato in franchising. Scopri prodotti, catalogo B2B e opportunità."
      image="/catalog/cat-primi.jpg"
    />
    <section className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-20">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-sm uppercase tracking-[0.3em] text-moss"
          >
            COMMPAQ · Società commerciale
          </motion.p>
          <h1
            data-testid="hero-heading"
            className="mt-8 font-display text-5xl font-medium leading-[0.98] tracking-tight text-forest sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>Il gluten-free</MaskedLine>
            <MaskedLine delay={0.3}>è un business.</MaskedLine>
            <MaskedLine delay={0.45} className="italic text-moss">
              Noi lo rendiamo semplice.
            </MaskedLine>
          </h1>
          <FadeUp delay={0.7} className="mt-10 max-w-md">
            <p className="text-base leading-relaxed text-forest/70">
              Commercializziamo i prodotti <strong className="font-medium text-forest">Propaq</strong> —
              84 referenze pronte in 5 minuti — e sviluppiamo <strong className="font-medium text-forest">Risorice</strong>,
              il ristorante automatizzato 100% gluten-free. Tu pensi a vendere: a prodotto,
              tecnologia e formazione pensiamo noi.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/propaq"
                data-testid="hero-cta-propaq"
                className="group inline-flex items-center gap-3 rounded-full bg-moss px-8 py-4 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-forest hover:shadow-xl hover:shadow-moss/30"
              >
                Scopri Propaq
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/affiliazione"
                data-testid="hero-cta-risorice"
                className="inline-flex items-center gap-3 rounded-full border border-forest/20 px-8 py-4 text-sm uppercase tracking-[0.15em] text-forest transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1 hover:border-moss hover:bg-moss/10"
              >
                Diventa affiliato
              </Link>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-5">
          <ClipReveal delay={0.5} className="relative">
            <ParallaxImage
              src={HERO_IMG}
              alt="Linguine allo scoglio Propaq, 100% gluten free"
              testId="hero-image"
              className="aspect-[3/4] rounded-2xl"
            />
            <motion.div
              data-testid="hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -left-6 bottom-10 flex items-center gap-3 rounded-full bg-forest/90 px-6 py-3 text-bone shadow-2xl backdrop-blur-md"
            >
              <Timer size={18} className="text-ochre" />
              <span className="text-sm uppercase tracking-[0.15em]">Pronto in 5 minuti</span>
            </motion.div>
          </ClipReveal>
        </div>
      </div>
    </section>

    <section data-testid="home-stats" className="border-b border-forest/10 bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-forest/10">
        {homeStats.map((s, i) => (
          <FadeUp key={s.label} delay={i * 0.08} className="px-6 py-12 text-center">
            <p className="font-display text-4xl font-extrabold text-moss sm:text-5xl">{s.value}</p>
            <p className="mt-3 text-xs uppercase tracking-[0.18em] text-forest/60">{s.label}</p>
          </FadeUp>
        ))}
      </div>
    </section>

    <Marquee
      items={["+30% di crescita annua", "84 referenze gluten free", "Pronto in 5 minuti", "Buoni SSN accettati"]}
      className="border-b border-forest/10"
    />

    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40">
      <FadeUp>
        <p className="text-sm uppercase tracking-[0.3em] text-moss">Chi siamo</p>
        <h2 className="mt-6 max-w-2xl font-display text-4xl font-medium leading-none text-forest sm:text-5xl">
          Una realtà commerciale, <span className="italic text-ochre">due anime.</span>
        </h2>
      </FadeUp>
      <FadeUp delay={0.15} className="mt-16 grid grid-cols-1 gap-16 lg:grid-cols-12">
        <p className="text-lg leading-relaxed text-forest/75 lg:col-span-7">
          <span className="float-left mr-4 font-display text-7xl font-semibold leading-[0.75] text-moss">C</span>
          OMMPAQ è la società commerciale che unisce prodotto e distribuzione: da un lato i prodotti
          Propaq, piatti pronti e sughi gluten free nati nei laboratori di Michelchef srl; dall'altro
          Risorice, la rete di negozi in franchising pensata per chi vuole fare impresa nel mercato
          senza glutine, in crescita costante. Un unico interlocutore, dalla produzione allo scaffale.
        </p>
        <div className="lg:col-span-5">
          <div className="border-l-2 border-ochre pl-8">
            <p className="font-display text-3xl italic leading-snug text-forest">
              "Portiamo il senza glutine di qualità dal laboratorio alla tavola, e dal sogno al negozio."
            </p>
          </div>
        </div>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-7xl px-6 pb-32 lg:px-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {chapters.map((c, i) => (
          <FadeUp key={c.n} delay={i * 0.15}>
            <Link
              to={c.to}
              data-testid={c.testId}
              className="group flex h-full flex-col justify-between border border-forest/10 bg-bone p-10 transition-[transform,box-shadow,background-color] duration-500 hover:-translate-y-2 hover:bg-white hover:shadow-2xl hover:shadow-moss/10 lg:p-14"
            >
              <div>
                <div className="flex items-start justify-between">
                  <span className="font-display text-6xl italic text-outline">{c.n}</span>
                  <c.icon size={28} className="text-sage" />
                </div>
                <h3 className="mt-10 font-display text-4xl font-medium text-forest">{c.title}</h3>
                <p className="mt-5 leading-relaxed text-forest/70">{c.text}</p>
              </div>
              <span className="mt-12 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-moss">
                {c.cta}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
              </span>
            </Link>
          </FadeUp>
        ))}
      </div>
    </section>

    <section className="bg-sage/15 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="text-sm uppercase tracking-[0.3em] text-moss">Risorice in pillole</p>
          <h2 className="mt-6 font-display text-4xl font-medium leading-none text-forest sm:text-5xl">
            Un negozio, <span className="italic text-moss">una comunità.</span>
          </h2>
        </FadeUp>
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          <FadeUp delay={0.1} className="md:col-span-2">
            <ParallaxImage
              src={STORE_IMG}
              alt="Ossobuco alla milanese Propaq"
              testId="home-store-image"
              className="aspect-[16/9] rounded-sm"
            />
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="flex h-full flex-col justify-between bg-forest p-10 text-bone">
              <p className="font-display text-2xl italic leading-snug">
                "Il cliente ordina dall'app, MICHELCHEF® cucina in 5 minuti, il Food-Box Wall
                consegna. Zero sala, zero cuochi, massima marginalità."
              </p>
              <Link
                to="/risorice"
                data-testid="home-franchise-cta"
                className="mt-10 inline-flex w-fit items-center gap-3 rounded-full border border-bone/30 px-7 py-3 text-sm uppercase tracking-[0.15em] transition-[transform,background-color,color] duration-300 hover:-translate-y-1 hover:bg-ochre hover:text-forest"
              >
                Il format Risorice
                <ArrowRight size={16} />
              </Link>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <ParallaxImage
              src={MARKET_IMG}
              alt="Il kiosk di ordinazione Risorice"
              testId="home-market-image"
              className="aspect-square rounded-sm"
            />
          </FadeUp>
          <FadeUp delay={0.25} className="md:col-span-2">
            <div className="flex h-full flex-col justify-center border border-forest/10 bg-bone p-10">
              <span className="font-display text-6xl italic text-outline">03</span>
              <h3 className="mt-6 font-display text-3xl font-medium text-forest">Un mercato che cresce</h3>
              <p className="mt-4 max-w-lg leading-relaxed text-forest/70">
                Il mercato gluten free cresce ogni anno a doppia cifra: celiaci, intolleranti e
                semplici appassionati cercano prodotti buoni e sicuri. Propaq e Risorice nascono
                per rispondere a questa domanda.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden bg-forest py-28 text-bone lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <h2 className="max-w-3xl font-display text-4xl font-medium leading-tight sm:text-5xl">
            Il mercato cresce del 30% l'anno. <span className="italic text-ochre">Tu dove sei?</span>
          </h2>
          <p className="mt-8 max-w-xl text-bone/70 leading-relaxed">
            Rivenditore, gastronomia o futuro affiliato Risorice: ogni mese di attesa è margine
            che lasci sul tavolo. Il primo passo è una conversazione di 15 minuti.
          </p>
          <Link
            to="/contatti"
            data-testid="home-contact-cta"
            className="mt-12 inline-flex items-center gap-3 rounded-full bg-ochre px-10 py-4 text-sm uppercase tracking-[0.15em] text-forest transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-ochre/20"
          >
            Parliamone
            <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </div>
    </section>
  </div>
);

export default Home;
